/* eslint-disable react/prop-types */
/* eslint-disable */
import React, { useState, useEffect } from 'react'
import * as localGdeTransactions from '../../../services/firestore/localGdeTransactions'
import { makeStyles } from '@material-ui/core/styles'
import {
  Button,
  IconButton,
  Typography,
} from '@material-ui/core'
import { Add, Edit as EditIcon } from '@material-ui/icons'
import { MaterialReactTable } from 'material-react-table'
import { useTranslation } from 'react-i18next'
import { getSlideoutInfo, getUser } from '../../../redux/global'
import { successToast, errorToast } from '../../../common/functions'
import ExportCsv from '../../Controls/ExportCsv'
import AddNewDeviceModal from './AddNewDeviceModal'

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
  },
  fieldSelection: {
    marginBottom: theme.spacing(2),
  },
  button: {
    marginTop: theme.spacing(2),
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
}))

const EdgeDeviceManagementFields = (props) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const [devices, setDevices] = useState([])
  const [loading, setLoading] = useState(true)
  const [addDeviceModalOpen, setAddDeviceModalOpen] = useState(false)
  const [editDeviceModalOpen, setEditDeviceModalOpen] = useState(false)
  const [editIndex, setEditIndex] = useState(null)

  const { userName } = getSlideoutInfo()
  const { workerId } = getUser()
  const userNameWithId = `${workerId} (${userName})`

  useEffect(() => {
    const unsubscribe = localGdeTransactions.getEdgeDevicesListener('listener', (snapshot) => {
      try {
        const data = snapshot.docs.map((doc) => {
          const rawDistricts = doc.data().assignedDistricts
          const assignedDistricts = rawDistricts == null
            ? {}
            : Array.isArray(rawDistricts)
              ? rawDistricts.reduce((acc, id) => ({ ...acc, [id]: id }), {})
              : rawDistricts
          return {
            id: doc.id,
            deviceName: doc.data().deviceName || '',
            deviceIP: doc.data().deviceIP || '',
            devicePort: doc.data().devicePort || '',
            assignedCountry: doc.data().assignedCountry || '',
            assignedDistricts,
            createdTS: doc.data().createdTS || '',
            updatedTS: doc.data().updatedTS || '',
            createdBy: doc.data().createdBy || '',
            updatedBy: doc.data().updatedBy || '',
          }
        })
        const sorted = data.sort((a, b) => {
          const dateA = new Date(a.updatedTS || a.createdTS || 0)
          const dateB = new Date(b.updatedTS || b.createdTS || 0)
          return dateB - dateA
        })
        setDevices(sorted)
        setLoading(false)
      } catch (error) {
        console.error('Error in edge devices listener:', error)
        setLoading(false)
      }
    })
    return () => unsubscribe()
  }, [])

  useEffect(() => {
    return () => {
      setAddDeviceModalOpen(false)
      setEditDeviceModalOpen(false)
      setEditIndex(null)
      setDevices([])
      setLoading(true)
    }
  }, [])

  const handleEditClick = (index) => {
    const device = devices[index]
    if (device) {
      setEditIndex(index)
      setEditDeviceModalOpen(true)
    }
  }

  const handleAddDevice = async (deviceData) => {
    try {
      const duplicate = devices.some(
        (d) => d.deviceIP === deviceData.deviceIP && d.deviceName === deviceData.deviceName
      )
      if (duplicate) {
        errorToast({
          message: t('dataEntry.duplicateValue'),
          title: t('toast.error'),
        })
        return
      }

      const dataToCreate = {
        deviceName: deviceData.deviceName,
        deviceIP: deviceData.deviceIP,
        devicePort: deviceData.devicePort,
        assignedCountry: deviceData.assignedCountry,
        assignedDistricts: deviceData.assignedDistricts && typeof deviceData.assignedDistricts === 'object' && !Array.isArray(deviceData.assignedDistricts)
          ? deviceData.assignedDistricts
          : (deviceData.assignedDistricts || []).reduce((acc, id) => ({ ...acc, [id]: id }), {}),
        createdTS: new Date().toISOString(),
        createdBy: userNameWithId,
        updatedTS: new Date().toISOString(),
        updatedBy: userNameWithId,
      }
      await localGdeTransactions.createEdgeDevice(dataToCreate)
      successToast({
        message: t('toast.deviceSaved') || 'Device saved successfully',
        title: t('toast.success'),
      })
    } catch (error) {
      console.error('Error adding device:', error)
      errorToast({
        message: t('toast.saveRowError'),
        title: t('toast.error'),
      })
    }
  }

  const handleEditDevice = async (deviceData) => {
    try {
      if (editIndex == null) {
        setEditDeviceModalOpen(false)
        return
      }
      const target = devices[editIndex]
      if (!target || !target.id) {
        setEditDeviceModalOpen(false)
        setEditIndex(null)
        return
      }

      const payload = {
        deviceName: deviceData.deviceName,
        deviceIP: deviceData.deviceIP,
        devicePort: deviceData.devicePort,
        assignedCountry: deviceData.assignedCountry,
        assignedDistricts: deviceData.assignedDistricts && typeof deviceData.assignedDistricts === 'object' && !Array.isArray(deviceData.assignedDistricts)
          ? deviceData.assignedDistricts
          : (deviceData.assignedDistricts || []).reduce((acc, id) => ({ ...acc, [id]: id }), {}),
        updatedTS: new Date().toISOString(),
        updatedBy: userNameWithId,
      }
      await localGdeTransactions.updateEdgeDevice(target.id, payload)
      successToast({
        message: t('toast.deviceUpdated') || 'Device updated successfully',
        title: t('toast.success'),
        autoHideDuration: 3000,
        isBlocking: false,
      })
    } catch (error) {
      console.error('Error updating device:', error)
      errorToast({
        message: t('toast.saveRowError'),
        title: t('toast.error'),
        autoHideDuration: 3000,
        isBlocking: false,
      })
    } finally {
      setEditDeviceModalOpen(false)
      setEditIndex(null)
    }
  }

  // District IDs already assigned to any device (for add modal: exclude from dropdown)
  const alreadyAssignedDistrictIds = devices.reduce((acc, device) => {
    const raw = device.assignedDistricts
    if (!raw) return acc
    const ids = Array.isArray(raw) ? raw : Object.keys(raw)
    ids.forEach((id) => acc.add(String(id)))
    return acc
  }, new Set())

  const columns = [
    {
      accessorKey: 'deviceName',
      header: t('tableColumns.deviceName'),
      Cell: ({ row }) =>
        <Typography>{row.original.deviceName}</Typography>
    },
    {
      accessorKey: 'deviceIP',
      header: t('tableColumns.deviceIP'),
      Cell: ({ row }) =>
        <Typography>{row.original.deviceIP}</Typography>
    },
    {
      accessorKey: 'devicePort',
      header: t('tableColumns.devicePort'),
      Cell: ({ row }) =>
        <Typography>{row.original.devicePort}</Typography>
    },
    {
      accessorKey: 'assignedCountry',
      header: t('tableColumns.assignedCountry'),
      Cell: ({ row }) =>
        <Typography>{row.original.assignedCountry}</Typography>
    },
    {
      accessorKey: 'assignedDistricts',
      header: t('tableColumns.assignedDistricts'),
      Cell: ({ row }) => {
        const d = row.original.assignedDistricts
        const text = d == null
          ? ''
          : Array.isArray(d)
            ? d.join(', ')
            : typeof d === 'object'
              ? Object.values(d).filter(Boolean).join(', ')
              : String(d)
        return <Typography>{text || '-'}</Typography>
      },
    },
    {
      accessorKey: 'updatedBy',
      header: t('tableColumns.updatedBy'),
      Cell: ({ row }) => <Typography>{row.original.updatedBy || '-'}</Typography>,
    },
    {
      accessorKey: 'actions',
      header: t('tableColumns.action'),
      Cell: (cell) => {
        const dataIndex = cell.row.index
        return (
          <div className={classes.actions}>
            <IconButton onClick={() => handleEditClick(dataIndex)} color='primary'>
              <EditIcon />
            </IconButton>
          </div>
        )
      },
    },
  ]

  return (
    <div className={classes.container}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginBottom: '20px',
        }}
      >
        <Button variant='contained' color='primary' onClick={() => setAddDeviceModalOpen(true)}>
          <Add style={{ marginRight: 8 }} />
          {t('dialogs.addNewDevice')}
        </Button>
      </div>
      <MaterialReactTable
        columns={columns}
        data={devices}
        enableRowSelection
        enableColumnFilters
        muiTableHeadCellProps={{
          sx: {
            color: '#58595B',
            fontWeight: 'bold',
          },
        }}
        muiTableBodyRowProps={({ row }) => ({
          sx: {
            backgroundColor: 'white',
          },
        })}
        renderTopToolbarCustomActions={({ table }) => {
          const isDisabled = !table.getIsSomeRowsSelected()
          const isNoData = devices?.length === 0
          const hasPageData = (table.getRowModel().rows?.length ?? 0) > 0
          return (
            <ExportCsv
              columns={columns}
              isDisabled={isDisabled}
              isNoData={isNoData}
              isNoPageData={hasPageData}
              table={table}
              hideExportSelectedData={false}
              hideExportPageData={false}
              enableExport={true}
            />
          )
        }}
        state={{ isLoading: loading }}
      />
      <AddNewDeviceModal
        open={addDeviceModalOpen}
        onClose={() => setAddDeviceModalOpen(false)}
        onAddCustomer={handleAddDevice}
        alreadyAssignedDistrictIds={Array.from(alreadyAssignedDistrictIds)}
      />
      {editIndex != null && devices[editIndex] && (
        <AddNewDeviceModal
          open={editDeviceModalOpen}
          onClose={() => {
            setEditDeviceModalOpen(false)
            setEditIndex(null)
          }}
          onAddCustomer={handleEditDevice}
          isEditMode={true}
          editData={devices[editIndex]}
        />
      )}
    </div>
  )
}

export default EdgeDeviceManagementFields
